import { useState, useEffect, useRef } from "react";
import { PenTool, Plus, Trash2, Edit2, FileSignature, AlertCircle, Loader2, Search, RefreshCw } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAdminTteOfficials, useCreateTteOfficial, useUpdateTteOfficial, useDeleteTteOfficial } from "@/services/adminTteOfficialService";
import type { TteOfficial } from "@/services/adminTteOfficialService";
import { useAdminCertificateTypes } from '@/services/adminCertificateTypeService';
import { usePeriods } from "@/services/usePeriods";
import toast from "react-hot-toast";

export default function TteOfficialsPage() {
    const [filterPeriod, setFilterPeriod] = useState<string>("all");
    const [filterSearch, setFilterSearch] = useState<string>("");

    // Pagination state
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);

    // Auto-select period
    const { data: periodsData } = usePeriods();
    const activePeriod = periodsData?.find((p: any) => p.is_active);
    const initialPeriodSet = useRef(false);

    useEffect(() => {
        if (activePeriod && !initialPeriodSet.current) {
            setFilterPeriod(String(activePeriod.period_id));
            initialPeriodSet.current = true;
        }
    }, [activePeriod]);

    // Fetch Data
    const { data: officials, isLoading, isFetching, refetch } = useAdminTteOfficials(filterPeriod);

    const { data: conferencesData } = useQuery({
        queryKey: ["conferencesList"],
        queryFn: async () => {
            const { data } = await axiosClient.get("/conference-billing/conferences");
            return data.data;
        }
    });

    // Filter Data Local
    const filteredOfficials = officials?.filter((off: TteOfficial) => {
        if (!filterSearch) return true;
        const s = filterSearch.toLowerCase();
        return (
            off.name?.toLowerCase().includes(s) ||
            off.niu?.toLowerCase().includes(s) ||
            off.position?.toLowerCase().includes(s)
        );
    }) || [];

    // Pagination Logic
    const totalItems = filteredOfficials.length;
    const totalPages = Math.ceil(totalItems / perPage);
    const startIndex = (page - 1) * perPage;
    const endIndex = Math.min(startIndex + perPage, totalItems);
    const currentOfficials = filteredOfficials.slice(startIndex, endIndex);

    // Mutations
    const createOfficial = useCreateTteOfficial();
    const updateOfficial = useUpdateTteOfficial();
    const deleteOfficial = useDeleteTteOfficial();

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [formData, setFormData] = useState({
        period_id: "",
        certificate_type: "all",
        conference_id: "",
        certificate_type_ids: [] as string[],
        name: "",
        niu: "",
        nip: "",
        position: "",
        affiliation: "",
        no_urut: "1",
        page: "1",
        tag_kordinat: "~"
    });

    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

    const { data: certificateTypes } = useAdminCertificateTypes();

    // "Konvensi" mengikuti sumber data yang sama dengan bucket TTE di backend
    // (CertificateController::sendTte): convention, mainroom, event_guest -> konvensi; sisanya -> konferensi.
    const isKonvensiRole = (ct: any) => ['convention', 'mainroom', 'event_guest'].includes(ct.source_type);
    const konvensiRoles = certificateTypes?.filter(isKonvensiRole) || [];
    const konferensiRoles = certificateTypes?.filter((ct: any) => !isKonvensiRole(ct)) || [];

    const handleOpenModal = (official?: TteOfficial) => {
        if (official) {
            setEditingId(official.id);
            setFormData({
                period_id: String(official.period_id),
                certificate_type: official.certificate_type || "all",
                conference_id: official.conference_id ? String(official.conference_id) : "",
                certificate_type_ids: official.certificate_type_ids ? official.certificate_type_ids.map(id => String(id)) : [],
                name: official.name || "",
                niu: official.niu || "",
                nip: official.nip || "",
                position: official.position || "",
                affiliation: official.affiliation || "",
                no_urut: String(official.no_urut || 1),
                page: official.page || "1",
                tag_kordinat: official.tag_kordinat || "~"
            });
        } else {
            setEditingId(null);
            setFormData({
                period_id: filterPeriod !== "all" ? filterPeriod : (activePeriod ? String(activePeriod.period_id) : ""),
                certificate_type: "all",
                conference_id: "",
                certificate_type_ids: [],
                name: "",
                niu: "",
                nip: "",
                position: "",
                affiliation: "",
                no_urut: String((officials?.length || 0) + 1),
                page: "1",
                tag_kordinat: `~${(officials?.length || 0) + 1}`
            });
        }
        setIsModalOpen(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.period_id || !formData.name || !formData.niu || !formData.position) {
            toast.error("Harap isi semua kolom yang wajib (Nama, NIU, Jabatan, Periode).");
            return;
        }

        const payload = {
            ...formData,
            certificate_type: formData.certificate_type as 'all' | 'konvensi' | 'konferensi',
            period_id: parseInt(formData.period_id),
            conference_id: formData.conference_id ? parseInt(formData.conference_id) : null,
            certificate_type_ids: formData.certificate_type_ids.length > 0 ? formData.certificate_type_ids.map(id => parseInt(id)) : null,
            no_urut: formData.no_urut ? parseInt(formData.no_urut) : 1
        };

        try {
            if (editingId) {
                await updateOfficial.mutateAsync({ id: editingId, payload });
                toast.success("Data penandatangan berhasil diperbarui!");
            } else {
                await createOfficial.mutateAsync(payload);
                toast.success("Data penandatangan berhasil ditambahkan!");
            }
            setIsModalOpen(false);
        } catch (error: any) {
            toast.error(error.message || "Terjadi kesalahan saat menyimpan data.");
        }
    };

    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-20 px-4 md:px-0">
            {/* 1. Header Card */}
            <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col xl:flex-row xl:items-center justify-between gap-6 relative overflow-hidden">
                {/* Dekorasi Background */}
                <div className="absolute -top-12 -right-12 p-12 opacity-5 pointer-events-none">
                    <PenTool className="w-64 h-64 text-indigo-600" />
                </div>

                <div className="flex items-start gap-5 relative z-10">
                    <div className="p-4 rounded-3xl bg-indigo-50 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-400 flex-shrink-0 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
                        <PenTool className="w-8 h-8" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-2">
                            Pengaturan Penandatangan
                        </h1>
                        <p className="text-base text-slate-500 dark:text-slate-400 max-w-2xl">
                            Kelola data pejabat/panitia yang akan menandatangani sertifikat kegiatan secara elektronik. Anda dapat mengatur penandatangan berdasarkan periode.
                        </p>
                    </div>
                </div>

            </div>

            {/* List */}
            <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden relative">

                {/* Table Header: Filters & Search */}
                <div className="p-5 md:p-6 flex flex-col lg:flex-row gap-5 bg-slate-50/50 dark:bg-slate-900/50 border-b border-slate-200/70 dark:border-slate-800 justify-between">
                    <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
                        <Select value={filterPeriod} onValueChange={setFilterPeriod}>
                            <SelectTrigger className="w-full sm:w-[260px] !h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl text-sm font-semibold shadow-sm focus:ring-indigo-500">
                                <SelectValue placeholder="Pilih Periode Aktif" />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl border-slate-200 dark:border-slate-800 shadow-xl">
                                <SelectItem value="all" className="font-medium cursor-pointer rounded-lg m-1">Semua Periode</SelectItem>
                                {periodsData?.map((p: any) => (
                                    <SelectItem key={p.period_id} value={String(p.period_id)} className="font-medium cursor-pointer rounded-lg m-1">
                                        {p.name} {p.is_active ? '(Aktif)' : ''}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        <div className="relative w-full sm:w-72">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                                placeholder="Cari nama atau NIU..."
                                value={filterSearch}
                                onChange={(e) => setFilterSearch(e.target.value)}
                                className="w-full pl-9 h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl text-sm focus-visible:ring-indigo-500 shadow-sm"
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-3 w-full lg:w-auto justify-end">
                        <Button
                            variant="outline"
                            onClick={() => refetch()}
                            disabled={isFetching}
                            className="h-11 px-4 rounded-xl border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-700 flex-1 sm:flex-none"
                        >
                            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
                        </Button>
                        <Button
                            onClick={() => handleOpenModal()}
                            className="h-11 px-5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm font-bold flex-1 sm:flex-none"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Tambah Data
                        </Button>
                    </div>
                </div>

                <div className="overflow-x-auto pb-4">
                    <table className="w-full text-left text-sm whitespace-nowrap">
                        <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider text-[11px]">
                            <tr>
                                <th className="px-6 py-4">No. Urut</th>
                                <th className="px-6 py-4">Nama Pejabat</th>
                                <th className="px-6 py-4">Berlaku Untuk</th>
                                <th className="px-6 py-4">NIU</th>
                                <th className="px-6 py-4">Jabatan</th>
                                <th className="px-6 py-4">Tag TTE</th>
                                <th className="px-6 py-4 text-right sticky right-0 bg-slate-50 dark:bg-slate-800/50 shadow-[-4px_0_6px_rgba(0,0,0,0.02)] z-10">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center text-slate-500 font-medium">
                                        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-indigo-500" />
                                        Memuat data...
                                    </td>
                                </tr>
                            ) : currentOfficials.length > 0 ? (
                                currentOfficials.map((off: TteOfficial) => (
                                    <tr key={off.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/20 transition-colors group">
                                        <td className="px-6 py-4 font-medium text-slate-600 dark:text-slate-400">
                                            {off.no_urut}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="font-bold text-slate-900 dark:text-white">{off.name}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col gap-1">
                                                <div className="flex gap-1">
                                                    {off.certificate_type === 'all' && <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded-md text-xs font-bold">Semua</span>}
                                                    {off.certificate_type === 'konvensi' && <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded-md text-xs font-bold">Konvensi</span>}
                                                    {off.certificate_type === 'konferensi' && <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-md text-xs font-bold w-fit">Konferensi</span>}
                                                </div>

                                                {off.certificate_type === 'konferensi' && off.conference_id && (
                                                    <span className="text-[10px] text-slate-500 max-w-[200px] truncate">
                                                        Track: {conferencesData?.find((c: any) => c.conference_id === off.conference_id)?.name || 'Tidak Ditemukan'}
                                                    </span>
                                                )}

                                                {off.certificate_type_ids && off.certificate_type_ids.length > 0 && (
                                                    <div className="flex flex-wrap gap-1 mt-1">
                                                        {off.certificate_type_ids.map(id => (
                                                            <span key={id} className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-md font-semibold w-fit">
                                                                Role: {certificateTypes?.find((ct: any) => String(ct.id) === String(id))?.name || 'Spesifik'}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-slate-600 dark:text-slate-400">
                                            {off.niu}
                                        </td>
                                        <td className="px-6 py-4 text-slate-600 dark:text-slate-400">
                                            {off.position}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="inline-flex items-center px-2 py-1 rounded bg-slate-100 text-slate-600 text-xs font-mono font-bold dark:bg-slate-800 dark:text-slate-400">
                                                Page: {off.page} | {off.tag_kordinat}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right sticky right-0 bg-white dark:bg-slate-900 group-hover:bg-slate-50 dark:group-hover:bg-slate-800/50 shadow-[-4px_0_6px_rgba(0,0,0,0.02)] z-10 transition-colors">
                                            <div className="flex items-center justify-end gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleOpenModal(off)}
                                                    className="text-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => setDeleteConfirmId(off.id)}
                                                    className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={7} className="px-6 py-16 text-center">
                                        <div className="flex flex-col items-center justify-center text-slate-400">
                                            <FileSignature className="w-12 h-12 mb-4 text-slate-300 dark:text-slate-600" />
                                            <p className="font-semibold text-base text-slate-600 dark:text-slate-300">Belum ada penandatangan</p>
                                            <p className="text-sm mt-1">Tambahkan pejabat penandatangan untuk periode ini.</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer Pagination */}
                {totalItems > 0 && !isLoading && (
                    <div className="p-4 md:p-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
                        <div className="flex items-center gap-3">
                            <span className="text-slate-500 font-medium text-xs sm:text-sm">
                                Menampilkan <span className="font-semibold text-slate-700 dark:text-slate-300">{startIndex + 1} - {endIndex}</span> dari <span className="font-semibold text-slate-700 dark:text-slate-300">{totalItems}</span> data
                            </span>
                        </div>

                        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                            <div className="flex items-center gap-2">
                                <span className="text-slate-500 text-xs sm:text-sm font-medium">Per halaman:</span>
                                <select
                                    value={perPage}
                                    onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                                    className="bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-lg px-2 py-1.5 outline-none focus:ring-1 focus:ring-indigo-500 font-bold text-slate-700 dark:text-slate-300 text-xs sm:text-sm shadow-sm"
                                >
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                    <option value="250">250</option>
                                    <option value="500">500</option>
                                </select>
                            </div>

                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    className="h-8 sm:h-9 px-3 sm:px-4 rounded-lg font-bold border-slate-200 dark:border-slate-700 text-xs sm:text-sm"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="mr-1 sm:mr-2"><path d="m15 18-6-6 6-6" /></svg>
                                    Prev
                                </Button>
                                <span className="text-xs sm:text-sm font-bold px-2 sm:px-3 text-slate-700 dark:text-slate-300">
                                    {page} / {totalPages || 1}
                                </span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    disabled={page === (totalPages || 1)}
                                    className="h-8 sm:h-9 px-3 sm:px-4 rounded-lg font-bold border-slate-200 dark:border-slate-700 text-xs sm:text-sm"
                                >
                                    Next
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="ml-1 sm:ml-2"><path d="m9 18 6-6-6-6" /></svg>
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal Form */}
            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogContent className="sm:max-w-2xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 max-h-[90vh] overflow-y-auto">
                    <form onSubmit={handleSubmit}>
                        <DialogHeader>
                            <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white">
                                {editingId ? "Edit Penandatangan" : "Tambah Penandatangan"}
                            </DialogTitle>
                            <DialogDescription className="text-slate-500 dark:text-slate-400">
                                Pastikan informasi NIU dan nama sudah sesuai untuk keperluan TTE.
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label className="font-bold">Periode <span className="text-red-500">*</span></Label>
                                <Select value={formData.period_id} onValueChange={(val) => setFormData({ ...formData, period_id: val })}>
                                    <SelectTrigger className="bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700">
                                        <SelectValue placeholder="Pilih Periode" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {periodsData?.map((p: any) => (
                                            <SelectItem key={p.period_id} value={String(p.period_id)}>
                                                {p.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="certificate_type" className="font-bold">Event (Tipe Sertifikat) <span className="text-red-500">*</span></Label>
                                    <Select value={formData.certificate_type} onValueChange={(v) => {
                                        const allowedIds = v === 'all'
                                            ? (certificateTypes?.map((ct: any) => String(ct.id)) || [])
                                            : (v === 'konvensi' ? konvensiRoles : konferensiRoles).map((ct: any) => String(ct.id));
                                        setFormData({
                                            ...formData,
                                            certificate_type: v,
                                            conference_id: v === 'konferensi' ? formData.conference_id : "",
                                            certificate_type_ids: formData.certificate_type_ids.filter(id => allowedIds.includes(id))
                                        });
                                    }}>
                                        <SelectTrigger className="bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700">
                                            <SelectValue placeholder="Pilih Tipe Event" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">Semua (Konvensi & Konferensi)</SelectItem>
                                            <SelectItem value="konvensi">Konvensi Saja</SelectItem>
                                            <SelectItem value="konferensi">Konferensi Saja</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Specific Certificate Type filter (e.g. Keynote Speaker) */}
                                <div className="space-y-2">
                                    <Label className="font-bold">Role Spesifik (Opsional)</Label>
                                    <div className="space-y-3 mt-2 bg-slate-50 dark:bg-slate-800/50 p-3 rounded-xl border border-slate-200 dark:border-slate-700">
                                        {(formData.certificate_type === 'all' || formData.certificate_type === 'konvensi') && konvensiRoles.length > 0 && (
                                            <div className="space-y-2">
                                                <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-indigo-100 text-indigo-700">Konvensi</span>
                                                <div className="grid grid-cols-2 gap-2">
                                                    {konvensiRoles.map((ct: any) => (
                                                        <div key={ct.id} className="flex items-center space-x-2">
                                                            <Checkbox
                                                                id={`role-${ct.id}`}
                                                                checked={formData.certificate_type_ids.includes(String(ct.id))}
                                                                onCheckedChange={(checked) => {
                                                                    const currentIds = [...formData.certificate_type_ids];
                                                                    if (checked) {
                                                                        currentIds.push(String(ct.id));
                                                                    } else {
                                                                        const idx = currentIds.indexOf(String(ct.id));
                                                                        if (idx > -1) currentIds.splice(idx, 1);
                                                                    }
                                                                    setFormData({ ...formData, certificate_type_ids: currentIds });
                                                                }}
                                                            />
                                                            <label
                                                                htmlFor={`role-${ct.id}`}
                                                                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer text-slate-700 dark:text-slate-300"
                                                            >
                                                                {ct.name} ({ct.code})
                                                            </label>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {(formData.certificate_type === 'all' || formData.certificate_type === 'konferensi') && konferensiRoles.length > 0 && (
                                            <div className="space-y-2">
                                                <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700">Konferensi</span>
                                                <div className="grid grid-cols-2 gap-2">
                                                    {konferensiRoles.map((ct: any) => (
                                                        <div key={ct.id} className="flex items-center space-x-2">
                                                            <Checkbox
                                                                id={`role-${ct.id}`}
                                                                checked={formData.certificate_type_ids.includes(String(ct.id))}
                                                                onCheckedChange={(checked) => {
                                                                    const currentIds = [...formData.certificate_type_ids];
                                                                    if (checked) {
                                                                        currentIds.push(String(ct.id));
                                                                    } else {
                                                                        const idx = currentIds.indexOf(String(ct.id));
                                                                        if (idx > -1) currentIds.splice(idx, 1);
                                                                    }
                                                                    setFormData({ ...formData, certificate_type_ids: currentIds });
                                                                }}
                                                            />
                                                            <label
                                                                htmlFor={`role-${ct.id}`}
                                                                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer text-slate-700 dark:text-slate-300"
                                                            >
                                                                {ct.name} ({ct.code})
                                                            </label>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <p className="text-xs text-slate-500">Pilih jika penandatangan HANYA untuk role tertentu (misal: Narasumber, Keynote Speaker). Biarkan tidak ada yang dicentang (kosong) jika untuk seluruh jenis sertifikat pada bucket Event yang dipilih di atas.</p>
                                </div>
                            </div>

                            {formData.certificate_type === 'konferensi' && (
                                <div className="space-y-2">
                                    <Label htmlFor="conference_id" className="font-bold">Track Konferensi (Opsional)</Label>
                                    <Select value={formData.conference_id} onValueChange={(val) => {
                                        const selectedConf = conferencesData?.find((c: any) => String(c.conference_id) === val);
                                        setFormData({
                                            ...formData,
                                            conference_id: val,
                                            name: selectedConf?.chief || formData.name,
                                            position: selectedConf ? "Conference Chair" : formData.position
                                        });
                                    }}>
                                        <SelectTrigger className="bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700">
                                            <SelectValue placeholder="Pilih Track Konferensi" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {conferencesData?.map((c: any) => (
                                                <SelectItem key={c.conference_id} value={String(c.conference_id)}>
                                                    {c.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <p className="text-xs text-slate-500 mt-1">Kosongkan jika pejabat ini menandatangani seluruh track konferensi secara default.</p>
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="name" className="font-bold">Nama Lengkap & Gelar <span className="text-red-500">*</span></Label>
                                <Input
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="Contoh: Prof. Dr. Budi Santoso, M.Pd."
                                    required
                                    className="bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="niu" className="font-bold">NIU <span className="text-red-500">*</span></Label>
                                <Input
                                    id="niu"
                                    value={formData.niu}
                                    onChange={(e) => setFormData({ ...formData, niu: e.target.value })}
                                    placeholder="Masukkan NIU"
                                    required
                                    className="bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="nip" className="font-bold">NIP (Opsional)</Label>
                                <Input
                                    id="nip"
                                    value={formData.nip}
                                    onChange={(e) => setFormData({ ...formData, nip: e.target.value })}
                                    placeholder="Masukkan NIP (Jika Ada)"
                                    className="bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label className="font-bold">Jabatan (Position) <span className="text-red-500">*</span></Label>
                                <Textarea
                                    value={formData.position}
                                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, position: e.target.value })}
                                    placeholder="Contoh: Rektor Universitas (Bisa di-enter untuk 2 baris)"
                                    required
                                    className="bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label className="font-bold">Afiliasi Bawah (Opsional)</Label>
                                <Input
                                    value={formData.affiliation}
                                    onChange={e => setFormData({ ...formData, affiliation: e.target.value })}
                                    placeholder="Contoh: Universitas Pendidikan Ganesha"
                                    className="bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700"
                                />
                                <p className="text-xs text-slate-500">Muncul di bagian paling bawah TTE</p>
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="no_urut" className="font-bold">No. Urut TTE</Label>
                                    <Input
                                        id="no_urut"
                                        type="number"
                                        value={formData.no_urut}
                                        onChange={(e) => setFormData({ ...formData, no_urut: e.target.value })}
                                        className="bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="page" className="font-bold">Hal. TTE</Label>
                                    <Input
                                        id="page"
                                        value={formData.page}
                                        onChange={(e) => setFormData({ ...formData, page: e.target.value })}
                                        className="bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="tag_kordinat" className="font-bold">Tag Posisi</Label>
                                    <Input
                                        id="tag_kordinat"
                                        value={formData.tag_kordinat}
                                        onChange={(e) => setFormData({ ...formData, tag_kordinat: e.target.value })}
                                        className="bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700"
                                    />
                                </div>
                            </div>
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-xl border-slate-300 dark:border-slate-700 font-bold">
                                Batal
                            </Button>
                            <Button type="submit" className="rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold" disabled={createOfficial.isPending || updateOfficial.isPending}>
                                {createOfficial.isPending || updateOfficial.isPending ? "Menyimpan..." : "Simpan"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Modal */}
            <Dialog open={deleteConfirmId !== null} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
                <DialogContent className="sm:max-w-md bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-rose-500" />
                            Konfirmasi Hapus
                        </DialogTitle>
                        <DialogDescription className="text-slate-500 dark:text-slate-400 mt-2">
                            Apakah Anda yakin ingin menghapus data penandatangan ini?
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={() => setDeleteConfirmId(null)} className="rounded-xl font-semibold border-slate-300 dark:border-slate-700">
                            Batal
                        </Button>
                        <Button
                            variant="destructive"
                            className="rounded-xl font-bold bg-rose-600 hover:bg-rose-700 text-white"
                            onClick={() => {
                                if (deleteConfirmId !== null) {
                                    toast.promise(deleteOfficial.mutateAsync(deleteConfirmId), {
                                        loading: 'Menghapus...',
                                        success: 'Berhasil dihapus!',
                                        error: 'Gagal menghapus.'
                                    });
                                }
                                setDeleteConfirmId(null);
                            }}
                            disabled={deleteOfficial.isPending}
                        >
                            Hapus Data
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
